import { useRef, useEffect, useState } from 'react';
import { create } from 'zustand';
import { useFlag } from '@unleash/proxy-client-react';
import { isCN } from '@monorepo/utils/locales';
import { useUserStore } from '@monorepo/store/useUserStore';

// @ts-nocheck
// Debounce a callback so that it cannot be called again until the
// specified timeout period has passed without it being called
const useDebounce = (callback, timeout, effectDeps) => {
    const timeoutId = useRef(0);
    useEffect(() => {
        clearTimeout(timeoutId.current);
        timeoutId.current = setTimeout(callback, timeout);
        return () => clearTimeout(timeoutId.current);
    }, effectDeps);
};

// @ts-nocheck
// sets height of elements in ref array equal to tallest one
function useEqualizeHeight(refsArray, setStateFn, effectDeps) {
    useEffect(() => {
        const elHeights = refsArray.current.map((ref) => ref.clientHeight);
        if (!elHeights?.length) {
            setStateFn(0);
        }
        else {
            setStateFn(Math.max(...elHeights));
        }
    }, effectDeps);
}

const useExampleStore = create()((set) => ({
    count: 1,
    inc: () => set((state) => ({ count: state.count + 1 })),
}));

// @ts-nocheck
// Handles the event of clicking outside of the referenced component.
// It's worth noting that because passed in handler is a new function on every render that will cause this
// effect callback/cleanup to run every render. It's not a big deal but to optimize you can wrap handler in
// useCallback before passing it into this hook.
// source: https://www.30secondsofcode.org/react/s/use-click-outside
const useOnClickOutside = (ref, handler) => {
    useEffect(() => {
        const listener = (event) => {
            // Do nothing if clicking ref's element or descendent elements
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        document.addEventListener("mousedown", listener);
        document.addEventListener("touchstart", listener);
        return () => {
            document.removeEventListener("mousedown", listener);
            document.removeEventListener("touchstart", listener);
        };
    }, [ref, handler]);
};

// @ts-nocheck
function useWindowSize() {
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [windowSize, setWindowSize] = useState({
        width: undefined,
        height: undefined,
    });
    useEffect(() => {
        // Handler to call on window resize and set window width/height to state
        function handleResize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }
        // Add event listener
        window.addEventListener("resize", handleResize);
        // Call handler right away so state gets updated with initial window size
        handleResize();
        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return windowSize;
}

// Loads a script in a <script/> tag appended to the page body
const useLoadScript = (url, onLoad = () => {}, onError = () => {}) => {
    useEffect(() => {
        if (Array.from(document.scripts).some((script) => script.src === url)) {
            // script is already added to the body
            onLoad();
            return;
        }
        const script = document.createElement("script");
        script.src = url;
        script.async = true;
        script.onload = onLoad;
        script.onerror = onError;
        document.body.appendChild(script);
    }, [url]);
};

// @ts-nocheck
const usePIPL = () => {
    const isPIPLM4On = useFlag("privacy-portal_pipl_m4");
    const countryCode = useUserStore((state) => state.clientCountryCode);
    if (isPIPLM4On) {
        return {
            hideGaming: false,
            hideEnterprise: false,
        };
    }
    else {
        return {
            hideGaming: countryCode === "CN" && !isCN(),
            hideEnterprise: isCN(),
        };
    }
};

export { useDebounce, useEqualizeHeight, useExampleStore, useLoadScript, useOnClickOutside, usePIPL, useWindowSize };
